import { useLazyQuery } from "@apollo/client";
import { clientCentralApiStrawberry, clientNeoFact } from "@neonumy/api-clients";
import * as Sentry from "@sentry/browser";
import { notification } from "antd";
import { routes } from "config/routes";
import { GET_ACCOUNTING_FIRM } from "queries/centralApi/centralApi";
import { GET_COMPANY, GET_COMPANY_SETTINGS } from "queries/centralApi/companies";
import { useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { onErrorGraphql } from "utils/onErrorGraphql";
import useCoreStore from "views/hooks/useCoreStore";
import useSettingsStore from "views/hooks/useSettingsStore";
import useVatsApi from "views/Settings/Accounting/components/Vats/hooks/useVatsApi";
import useCurrencyApi from "views/Settings/General/components/Formats/Currency/hooks/useCurrencyApi";

export function useFieldsSelection() {
  const { t } = useTranslation();
  const lastLoadedCompanyId = useRef<ID | null>(null);

  const { listVats } = useVatsApi();
  const { listCurrenciesOptions } = useCurrencyApi();

  const setCompanySettingsLoading = useCoreStore(state => state.setCompanySettingsLoading);
  const setPaymentOptions = useSettingsStore(state => state.setPaymentOptions);
  const setBank = useSettingsStore(state => state.setBank);
  const setCompanySettings = useCoreStore(state => state.setCompanySettings);
  const setCompanySelected = useCoreStore(state => state.setCompanySelected);
  const setAccountingFirmSelected = useCoreStore(state => state.setAccountingFirmSelected);
  const setCompanyData = useCoreStore(state => state.setCompanyData);
  const setCurrency = useCoreStore(state => state.setCurrency);

  const [getCompanySetting, { data: getCompanySettingData, error: getCompanySettingError }] =
    useLazyQuery<GetCompanySettingResponse, GetVarTypeID>(GET_COMPANY_SETTINGS, {
      client: clientNeoFact,
    });

  useEffect(() => {
    if (getCompanySettingError) onErrorGraphql(getCompanySettingError);
  }, [getCompanySettingError]);

  useEffect(() => {
    const getCompanySetting = getCompanySettingData?.getCompanySetting;
    if (!getCompanySetting) return;

    setCompanySettings(getCompanySetting as unknown as Parameters<typeof setCompanySettings>[0]);
    const mainCurrency = getCompanySetting?.currencySettings?.find(item => item?.isMainCurrency);
    if (mainCurrency) {
      setCurrency(mainCurrency);
    }
    const filteredMethods =
      getCompanySetting?.defaultPaymentMethods?.filter(item => item.code !== "RE") ?? [];
    const paymentOptions = filteredMethods.map(item => ({
      label: item.name,
      value: String(item.id),
    }));
    setBank(getCompanySetting?.defaultBank);
    setPaymentOptions(paymentOptions);
    setCompanySettingsLoading(false);
  }, [getCompanySettingData]);

  const [getAccountingFirm, { data: getAccountingFirmData }] = useLazyQuery<
    GetAccountingFirmResponseType,
    GetAccountingFirmInputType
  >(GET_ACCOUNTING_FIRM, {
    client: clientCentralApiStrawberry,
  });

  useEffect(() => {
    if (getAccountingFirmData?.getAccountingFirm) {
      const { id, name } = getAccountingFirmData.getAccountingFirm;
      setAccountingFirmSelected({ id, name });
    }
  }, [getAccountingFirmData]);

  const [getCompanyQuery, { loading: getCompanyLoading, error: getCompanyQueryError }] =
    useLazyQuery<GetCompanyData, GetCompanyVars>(GET_COMPANY, {
      client: clientCentralApiStrawberry,
    });

  useEffect(() => {
    if (!getCompanyQueryError) return;
    const errors = getCompanyQueryError.graphQLErrors || getCompanyQueryError.networkError;
    if (errors) {
      Sentry.captureException(errors);
      routes.navigate(`/error`, { replace: true });
    }
  }, [getCompanyQueryError]);

  const handleGetCompanyResponse = async ({ getCompany }: GetCompanyData) => {
    if (getCompany) {
      setCompanySettingsLoading(true);
      const { accountingFirm } = getCompany;
      setCompanyData(getCompany as unknown as Parameters<typeof setCompanyData>[0]);
      setCompanySelected({
        id: getCompany.id as string,
        name: getCompany.name,
        internalReference: getCompany.internalReference,
      });
      if (accountingFirm) {
        setAccountingFirmSelected({ id: accountingFirm.id, name: accountingFirm.name });
      }

      const [, , response] = await Promise.all([
        listVats({
          variables: {
            filter: {
              companyId: getCompany.id,
              activeOnly: true,
            },
            orderBy: ["rate"],
          },
        }),
        listCurrenciesOptions({
          variables: {
            filter: { companyId: getCompany.id, isActive: true },
            limit: 10,
            offset: 0,
          },
        }),
        getCompanySetting({ variables: { id: getCompany.id } }),
      ]);
      if (!response.data?.getCompanySetting.setupFinished) {
        routes.navigate(`/company/${getCompany.id}/setup`);
      }
    } else {
      routes.navigate("/", { replace: true });
      notification.error({
        message: t("Company not found"),
      });
    }
  };

  const getCompany = async (options: { variables: { id: ID } }) => {
    const companyId = options.variables.id;

    if (getCompanyLoading || lastLoadedCompanyId.current === companyId) {
      return;
    }
    lastLoadedCompanyId.current = companyId;
    const response = await getCompanyQuery(options);
    if (response.data) {
      await handleGetCompanyResponse(response.data);
    }
    return response;
  };

  return {
    getCompany,
    getAccountingFirm,
  };
}
